// src/pages/ChangelogScreen.jsx
import {
  BugReport as BugReportIcon,
  CalendarToday as CalendarIcon,
  CircleRounded,
  ExpandMore as ExpandMoreIcon,
  NewReleases as NewReleasesIcon,
  TrendingUp as TrendingUpIcon,
  UpdateOutlined,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  Chip,
  Container,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { size } from "lodash";
import { useState } from "react";
import Flx from "../../components/layout/Flx";
import Htag from "../../components/typography/Htag";
import Txt from "../../components/typography/Txt";
import { changelogData } from "./data/changelogData";

const ChangelogScreen = () => {
  const theme = useTheme();

  const [expandedVersions, setExpandedVersions] = useState([0]);

  const handleAccordionChange = (version) => (event, isExpanded) => {
    setExpandedVersions((prev) =>
      isExpanded ? [...prev, version] : prev.filter((v) => v !== version)
    );
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Flx center column sx={{ textAlign: "center", mb: 6 }} gap={1}>
        <Htag h1>Changelog</Htag>
        <Txt secondary sx={{ mb: 3 }}>
          Stay up to date with the latest improvements to your LOS platform
        </Txt>
      </Flx>

      {/* Changelog Entries */}
      <Flx column gap={2}>
        {changelogData?.map((release, index) => (
          <>
            {index <= size(changelogData) - 1 && index !== 0 && <Divider />}
            <Accordion
              key={index}
              expanded={expandedVersions.includes(index)}
              onChange={handleAccordionChange(index)}
              sx={{
                px: 2,
                m: "0 !important",
                boxShadow: "none",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  "& .MuiAccordionSummary-content": {
                    alignItems: "center",
                    gap: 2,
                    flexWrap: "wrap",
                  },
                }}
              >
                <Flx
                  column
                  sx={{
                    // display: "flex",
                    // alignItems: "center",
                    gap: 2,
                    // flexGrow: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      // ml: "auto",
                    }}
                  >
                    <CalendarIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {new Date(release.releaseDate).toLocaleDateString(
                        "en-US",
                        {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        }
                      )}
                    </Typography>
                  </Box>
                  <Htag h2>{release.summary}</Htag>
                </Flx>
              </AccordionSummary>

              <AccordionDetails sx={{ pb: 3, pt: 0 }}>
                {/* <Divider sx={{ mb: 3 }} /> */}

                <Stack spacing={5} sx={{ mt: 3 }}>
                  {/* Features */}
                  {release.changes.features?.length > 0 && (
                    <ChangeSection
                      title="New Features"
                      icon={<NewReleasesIcon color="primary" />}
                      items={release.changes.features}
                      color="primary"
                    />
                  )}

                  {/* Improvements */}
                  {release.changes.improvements?.length > 0 && (
                    <ChangeSection
                      title="Improvements"
                      icon={<TrendingUpIcon color="success" />}
                      items={release.changes.improvements}
                      color="success"
                      showImpact
                    />
                  )}

                  {/* Bug Fixes */}
                  {release.changes.bugFixes?.length > 0 && (
                    <ChangeSection
                      title="Bug Fixes"
                      icon={<BugReportIcon color="error" />}
                      items={release.changes.bugFixes}
                      color="error"
                      showSeverity
                    />
                  )}

                  {/* Investigating */}
                  {release.changes.investigating?.length > 0 && (
                    <ChangeSection
                      title="Investigating & Coming Soon"
                      icon={<UpdateOutlined color="warning" />}
                      items={release.changes.investigating}
                      color="warning"
                    />
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>
          </>
        ))}
      </Flx>
    </Container>
  );
};

// Sub-component for each change section
const ChangeSection = ({
  title,
  icon,
  items,
  color,
  showImpact,
  showSeverity,
}) => {
  const theme = useTheme();

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        {icon}
        <Htag h3 sx={{ fontWeight: 700 }}>
          {title} ({size(items)})
        </Htag>
      </Box>

      <Stack spacing={2}>
        {items.map((item, idx) => (
          <Card
            key={idx}
            variant="outlined"
            sx={{
              border: "1px solid #d9d9d9",
              // border: "1px solid #dddddd",
              boxShadow: "none",
              borderLeft: 4,
              borderLeftColor: `${color}.main`,
              // "&:hover": {
              //   boxShadow: theme.shadows[2],
              //   backgroundColor: alpha(theme.palette[color].main, 0.02),
              // },
            }}
          >
            <>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    {item.title}
                  </Typography>
                  {item?.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      {item.description}
                    </Typography>
                  )}

                  {item.details && (
                    <List dense sx={{ mt: 1 }}>
                      {item.details.map((detail, dIdx) => (
                        <ListItem key={dIdx} sx={{ pl: 0, py: 0.25 }}>
                          <ListItemIcon sx={{ minWidth: 14 }}>
                            <CircleRounded
                              fontSize="small"
                              color="inherit"
                              sx={{ fontSize: "6px" }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={detail}
                            primaryTypographyProps={{ variant: "body2" }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Box>

                {showImpact && item.impact && (
                  <Chip
                    label={item.impact}
                    size="small"
                    color={
                      item.impact === "high"
                        ? "error"
                        : item.impact === "medium"
                          ? "warning"
                          : "success"
                    }
                    variant="outlined"
                  />
                )}

                {showSeverity && item.severity && (
                  <Chip
                    label={item.severity}
                    size="small"
                    color={
                      item.severity === "high"
                        ? "error"
                        : item.severity === "medium"
                          ? "warning"
                          : "info"
                    }
                    variant="outlined"
                  />
                )}
              </Box>
            </>
          </Card>
        ))}
      </Stack>
    </Box>
  );
};

export default ChangelogScreen;
